import React, { useMemo, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import {
  Card,
  Tag,
  Button,
  EmptyState,
  Input,
  Textarea,
} from "../../../../design-system";
import { IconSparkles } from "../../../../design-system/icons/icons";
import {
  OutreachDraft,
  DraftMessage,
  DraftStatus,
  OutreachRunSummary,
} from "../../../../types/OutreachMetrics";
import { reviewOutreachDraft } from "../../../../database/outreach/outreachApi";
import { SectionSpacing } from "./ui";

// Review queue for the daily outreach run's drafts. The run writes every
// personalized message here as pending (or held when it found no anchor); this
// tab is where a human approves, edits, or rejects them. Approved drafts ship
// on the run's next pass — nothing in this tab sends anything directly.

const KIND_LABEL: Record<string, string> = {
  li_dm: "LinkedIn",
  ig_dm: "Instagram",
  x_dm: "X",
  email: "Email",
};

const SLOT_LABEL: Record<string, string> = {
  connection_note: "Connection note",
  followup_1: "Follow-up 1",
  followup_2: "Follow-up 2",
  dm: "Cold DM",
  email_1: "Email 1",
  email_2: "Email 2",
  email_3: "Email 3",
};

const slotLabel = (slot: string) =>
  SLOT_LABEL[slot] ??
  slot.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const STATUS_TAG: Record<
  DraftStatus,
  { label: string; variant: "neutral" | "accent" | "success" | "warning" | "danger" | "info" }
> = {
  pending: { label: "Pending", variant: "accent" },
  held: { label: "Held — needs anchor", variant: "warning" },
  approved: { label: "Approved", variant: "info" },
  shipping: { label: "Shipping", variant: "info" },
  rejected: { label: "Rejected", variant: "danger" },
  shipped: { label: "Shipped", variant: "success" },
  error: { label: "Ship failed", variant: "danger" },
};

const QueueHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const QueueSummary = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-right: auto;
`;

const DraftHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const LeadName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const HandleText = styled.a`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  text-decoration: none;

  &:hover {
    color: var(--color-text-secondary);
    text-decoration: underline;
  }
`;

const MetaText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-left: auto;
`;

const AnchorLine = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-top: var(--space-3);

  strong {
    color: var(--color-text);
    font-weight: var(--font-weight-medium);
  }
`;

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: var(--space-4);
`;

const MessageBlock = styled.div`
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
`;

const MessageSlot = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: var(--space-2);
`;

const MessageText = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  white-space: pre-wrap;
  line-height: var(--line-height-relaxed, 1.5);
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-top: var(--space-4);
  flex-wrap: wrap;
`;

const ErrorText = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-danger);
  margin-top: var(--space-3);
`;

const ReviewNoteLine = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-top: var(--space-3);

  strong {
    color: var(--color-text);
    font-weight: var(--font-weight-medium);
  }
`;

const EditField = styled.div`
  margin-top: var(--space-3);
`;

const FieldLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-2);
`;

// One draft card: header (who/where/status), anchor, messages, actions. Edit
// mode swaps the message blocks for textareas and the anchor for an input —
// saving an edit happens as part of Approve, matching the server's single
// review action.
const DraftCard: React.FC<{
  draft: OutreachDraft;
  accountId: string;
  onUpdated: (draft: OutreachDraft) => void;
}> = ({ draft, accountId, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [anchor, setAnchor] = useState(draft.anchor ?? "");
  const [messages, setMessages] = useState<DraftMessage[]>(draft.messages ?? []);
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const actionable =
    draft.status === "pending" ||
    draft.status === "held" ||
    draft.status === "error" ||
    draft.status === "approved";
  // Approved drafts wait for the next run; re-approving is pointless, but
  // rejecting one before it ships is still allowed.
  const canApprove = draft.status !== "approved";

  const statusTag = STATUS_TAG[draft.status] ?? {
    label: draft.status,
    variant: "neutral" as const,
  };

  const review = async (status: "approved" | "rejected") => {
    setBusy(status === "approved" ? "approve" : "reject");
    setError(null);
    try {
      const updated = await reviewOutreachDraft({
        accountId,
        draftId: draft.id,
        status,
        // Only send edits when the user actually opened the editor, so an
        // untouched approve keeps the run's exact text.
        ...(editing ? { messages, anchor } : {}),
      });
      onUpdated(updated);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || "Review failed.");
    } finally {
      setBusy(null);
    }
  };

  const editMessage = (index: number, content: string) =>
    setMessages((prev) =>
      prev.map((m, i) => (i === index ? { ...m, content } : m))
    );

  return (
    <Card padding="md">
      <DraftHead>
        <Tag size="sm" variant="neutral">
          {KIND_LABEL[draft.kind] ?? draft.kind}
        </Tag>
        <LeadName>{draft.leadName}</LeadName>
        {draft.handle && (
          <HandleText
            href={
              /^https?:/.test(draft.handle)
                ? draft.handle
                : undefined
            }
            target="_blank"
            rel="noreferrer"
          >
            {draft.handle}
          </HandleText>
        )}
        <Tag size="sm" variant={statusTag.variant}>
          {statusTag.label}
        </Tag>
        <MetaText>
          {draft.createdAt ? moment(draft.createdAt).fromNow() : ""}
        </MetaText>
      </DraftHead>

      {editing ? (
        <EditField>
          <FieldLabel>
            Anchor — the one real profile detail these messages are built on
          </FieldLabel>
          <Input
            value={anchor}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAnchor(e.target.value)
            }
            placeholder='e.g. her "Reto 21 días" program'
          />
        </EditField>
      ) : (
        <AnchorLine>
          {draft.anchor ? (
            <>
              Anchor: <strong>{draft.anchor}</strong>
            </>
          ) : (
            "No anchor — add one (edit) before this can be approved."
          )}
        </AnchorLine>
      )}

      <MessageList>
        {(editing ? messages : draft.messages ?? []).map((m, i) => (
          <MessageBlock key={`${m.slot}-${i}`}>
            <MessageSlot>
              {slotLabel(m.slot)}
              {typeof m.stepNumber === "number" ? ` · step ${m.stepNumber}` : ""}
            </MessageSlot>
            {editing ? (
              <Textarea
                value={m.content}
                rows={3}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  editMessage(i, e.target.value)
                }
              />
            ) : (
              <MessageText>{m.content}</MessageText>
            )}
          </MessageBlock>
        ))}
      </MessageList>

      {draft.status === "error" && draft.error && (
        <ErrorText>Ship failed: {draft.error}</ErrorText>
      )}

      {/* Why it died. The daily run can retire a draft it knows can never ship
          (reject_outreach_draft, reviewedBy "automated-run") and the reason it
          gave lands here, so a rejected card is never a bare status tag. */}
      {draft.status === "rejected" && draft.reviewNote && (
        <ReviewNoteLine>
          Rejected
          {draft.reviewedBy === "automated-run"
            ? " by the daily run"
            : draft.reviewedBy
            ? " in the dashboard"
            : ""}
          : <strong>{draft.reviewNote}</strong>
        </ReviewNoteLine>
      )}
      {error && <ErrorText>{error}</ErrorText>}

      {actionable && (
        <ActionRow>
          {canApprove && (
            <Button
              size="sm"
              onClick={() => review("approved")}
              disabled={busy !== null || (!editing && !draft.anchor)}
              isLoading={busy === "approve"}
            >
              {draft.status === "error" ? "Approve retry" : "Approve"}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => review("rejected")}
            disabled={busy !== null}
            isLoading={busy === "reject"}
          >
            Reject
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing((v) => !v);
              setAnchor(draft.anchor ?? "");
              setMessages(draft.messages ?? []);
            }}
            disabled={busy !== null}
          >
            {editing ? "Cancel edit" : "Edit"}
          </Button>
        </ActionRow>
      )}
    </Card>
  );
};

// One line about the daily run: when it last ran and its totals, summed
// across channels. Absent until the pipeline has run at least once.
const runSummaryLine = (run: OutreachRunSummary): string => {
  const per = run.perChannel ?? {};
  const sum = (k: string) =>
    Object.values(per).reduce((n, c: any) => n + (Number(c?.[k]) || 0), 0);
  const parts = [
    `${sum("drafted")} drafted`,
    `${sum("held")} held`,
    `${sum("shipped")} shipped`,
  ];
  const errors = sum("shipErrors");
  if (errors) parts.push(`${errors} ship error${errors === 1 ? "" : "s"}`);
  return `Last run ${run.at ? moment(run.at).fromNow() : "unknown"} · ${parts.join(
    " · "
  )}`;
};

// The tab. Needs-action drafts (pending, held, error) show by default;
// approved-waiting-to-ship and recent history sit behind toggles so the
// default view stays focused on what actually needs a decision.
const ReviewQueue: React.FC<{
  drafts: OutreachDraft[];
  lastRun: OutreachRunSummary | null;
  accountId: string;
  onUpdated: (draft: OutreachDraft) => void;
}> = ({ drafts, lastRun, accountId, onUpdated }) => {
  const [showHistory, setShowHistory] = useState(false);

  const { needsAction, waiting, history } = useMemo(() => {
    const needsAction = drafts.filter(
      (d) => d.status === "pending" || d.status === "held" || d.status === "error"
    );
    const waiting = drafts.filter(
      (d) => d.status === "approved" || d.status === "shipping"
    );
    const history = drafts.filter(
      (d) => d.status === "shipped" || d.status === "rejected"
    );
    return { needsAction, waiting, history };
  }, [drafts]);

  if (drafts.length === 0) {
    return (
      <SectionSpacing>
        {lastRun && <QueueSummary>{runSummaryLine(lastRun)}</QueueSummary>}
        <EmptyState
          icon={<IconSparkles size={20} />}
          title="No drafts to review"
          description="The daily outreach run queues personalized messages here for approval before anything ships. Nothing is waiting right now."
        />
      </SectionSpacing>
    );
  }

  return (
    <SectionSpacing>
      <QueueHead>
        <QueueSummary>
          {lastRun ? `${runSummaryLine(lastRun)} · ` : ""}
          {needsAction.length} to review · {waiting.length} approved, shipping
          on the next run · {history.length} resolved
        </QueueSummary>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowHistory((v) => !v)}
        >
          {showHistory ? "Hide resolved" : "Show resolved"}
        </Button>
      </QueueHead>

      {needsAction.length === 0 && (
        <EmptyState
          icon={<IconSparkles size={20} />}
          title="All reviewed"
          description={
            waiting.length > 0
              ? `Nothing needs a decision. ${waiting.length} approved draft${
                  waiting.length === 1 ? "" : "s"
                } will ship on the run's next pass.`
              : "Nothing needs a decision right now."
          }
        />
      )}

      {needsAction.map((d) => (
        <DraftCard
          key={d.id}
          draft={d}
          accountId={accountId}
          onUpdated={onUpdated}
        />
      ))}

      {waiting.map((d) => (
        <DraftCard
          key={d.id}
          draft={d}
          accountId={accountId}
          onUpdated={onUpdated}
        />
      ))}

      {showHistory &&
        history.map((d) => (
          <DraftCard
            key={d.id}
            draft={d}
            accountId={accountId}
            onUpdated={onUpdated}
          />
        ))}
    </SectionSpacing>
  );
};

export default ReviewQueue;
